import { MAX_CERTIFICATE_PAGE_BYTES, MAX_INVOICE_PDF_BYTES } from '@felix/domain';
import {
  downloadFile,
  type OfficeInvoiceTargetView,
  type SupplierAccountData,
  type SupplierAccountSource,
  type SupplierHistoryData,
  type SupplierStatementData,
} from '@felix/ui';
import { ApiError, api, officeApi } from './api.js';
import { tusUpload } from './tus-upload.js';

/**
 * Dekont yüklemesinin amacı: sunucu sınırı (25 MB) ve ödemeye bağlamada amaç denetimi buna göre
 * (`apps/server/src/office/uploads.ts` `SUPPLIER_PAYMENT_RECEIPT_UPLOAD_PURPOSE`; ADR 0119 §4).
 */
const RECEIPT_PURPOSE = 'supplier_payment_receipt';
/** Ofisin tedarikçi adına girdiği faturanın PDF'i (8 MB; ADR 0119 Ek 2026-10-24). */
const INVOICE_PURPOSE = 'supplier_invoice';
const id = encodeURIComponent;

/**
 * Ofiste tedarikçi carisi (ADR 0119): kart penceresinin uçları. Fatura PDF'i ve dekont oturumlu
 * ofis adresinden açılır; ekstre PDF ve Excel istemcide üretilir.
 */
export function officeSupplierAccountSource(owner?: string): SupplierAccountSource {
  return {
    get: (supplierId) => api<SupplierAccountData>(`/office/suppliers/${id(supplierId)}/account`),
    history: (supplierId) =>
      api<SupplierHistoryData>(`/office/suppliers/${id(supplierId)}/history`),
    statement: (supplierId, range) =>
      api<SupplierStatementData>(
        `/office/suppliers/${id(supplierId)}/statement?${new URLSearchParams(range).toString()}`,
      ),
    review: (invoiceId, input) =>
      api<SupplierAccountData>(`/office/supplier-invoices/${id(invoiceId)}/review`, {
        method: 'POST',
        body: input,
      }),
    pay: async (supplierId, input) => {
      // Sunucu da denetler; büyük dosya hiç gönderilmez
      if (input.receipt.size <= 0 || input.receipt.size > MAX_CERTIFICATE_PAGE_BYTES) {
        throw new ApiError('FILE_TOO_LARGE', 413);
      }
      const uploadId = await tusUpload(input.receipt, {
        purpose: RECEIPT_PURPOSE,
        ...(owner ? { owner } : {}),
        onProgress: () => undefined,
      });
      const { receipt, ...payment } = input;
      const result = await api<{ id: string; account: SupplierAccountData }>(
        `/office/suppliers/${id(supplierId)}/payments`,
        {
          method: 'POST',
          body: { ...payment, receipt: { uploadId, fileName: receipt.name } },
        },
      );
      return result.account;
    },
    invoiceTargets: async (supplierId) =>
      (
        await api<{ items: OfficeInvoiceTargetView[] }>(
          `/office/suppliers/${id(supplierId)}/invoice-targets`,
        )
      ).items,
    enterInvoice: async (supplierId, input) => {
      if (input.file.size <= 0 || input.file.size > MAX_INVOICE_PDF_BYTES) {
        throw new ApiError('FILE_TOO_LARGE', 413);
      }
      const uploadId = await tusUpload(input.file, {
        purpose: INVOICE_PURPOSE,
        ...(owner ? { owner } : {}),
        onProgress: () => undefined,
      });
      const result = await api<{ id: string; account: SupplierAccountData }>(
        `/office/suppliers/${id(supplierId)}/invoices`,
        {
          method: 'POST',
          body: {
            purchaseOrderId: input.purchaseOrderId,
            invoice: input.invoice,
            file: { uploadId, fileName: input.file.name },
          },
        },
      );
      return result.account;
    },
    decide: (paymentId, input) =>
      api<SupplierAccountData>(`/office/supplier-payments/${id(paymentId)}/decision`, {
        method: 'POST',
        body: input,
      }),
    reverse: (paymentId, input) =>
      api<SupplierAccountData>(`/office/supplier-payments/${id(paymentId)}/reverse`, {
        method: 'POST',
        body: input,
      }),
    invoiceFileUrl: (invoiceId) => `/api/office/supplier-invoices/${id(invoiceId)}/file`,
    receiptUrl: (paymentId) => `/api/office/supplier-payments/${id(paymentId)}/receipt`,
    companyName: async () => {
      try {
        return (await officeApi.branding()).branding?.name ?? '';
      } catch {
        return '';
      }
    },
    download: (fileName, bytes, type) => downloadFile(fileName, bytes, type),
  };
}
